import React, { useEffect, useState } from "react";

import { Icon } from "@iconify/react/dist/iconify.js";
import { CircularProgress, Modal, Tooltip, useTheme } from "@mui/material";
import { GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import { usePostValidateDuplications } from "api/usePostValidateDuplications";
import { Alerts } from "components/shared/Alerts/Alerts";
import SearchField from "components/shared/SearchField/SearchField";
import DocumentGridBreadcrumb from "pages/DocumentManagementView/DocumentGridBreadcrumb";
import { Link } from "react-router-dom";
import { AlertColor, OVERRIDE_MESSAGE, SOURCE_TEAMS, TYPE_FOLDER } from "util/constants";
import { DocumentData } from "util/interfaces/DocumentData";

import CreateFolderStep from "../UploadFile/CreateFolderStep";

import { usePostDeleteBlobs } from "@/api/useDeleteBlob";
import { DocumentIcon } from "@/components/DocumentManagementView/DocumentGrid/DocumentIcon";
import CustomGrid from "@/components/shared/CustomGrid/CustomGrid";
import axios from "@/configs/axios";
import {
  getBlobTree,
  getDocumentsWithChildren,
} from "@/pages/DocumentManagementView/DocumentGrid.utils";

interface MoveModalProps {
  callbackClose: (_update: boolean) => void;
  selectedDocuments: DocumentData[];
  availableFolders: DocumentData[] | undefined;
  currentPath: { id: string; name: string }[];
}
export const MoveModal: React.FC<MoveModalProps> = ({
  callbackClose,
  selectedDocuments,
  availableFolders,
  currentPath,
}) => {
  const [originalAvailableFolders, setOriginalAvailableFolders] = useState<DocumentData[]>(
    availableFolders || []
  );
  const { mutateAsync: callValidateDuplicate } = usePostValidateDuplications({});

  // Helper functions
  const hasTeamsSource = (doc: DocumentData): boolean => {
    return doc.source === SOURCE_TEAMS;
  };

  const [originalTree, setOriginalTree] = useState<DocumentData[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [alertColor, setAlertColor] = useState<AlertColor>(AlertColor.Info);
  const [alertMessage, setAlertMessage] = useState<string>("");
  const [searchText, setSearchText] = useState<string>("");
  const [selectedPath, setSelectedPath] = useState<{ id: string; name: string }[]>(currentPath);
  const [parentId, setParentId] = useState<string | null>(
    currentPath.length === 0 ? null : (currentPath.at(-1)?.id ?? null)
  );
  const { mutateAsync: callDeleteBlobs } = usePostDeleteBlobs();
  const [duplicatedFiles, setDuplicatedFiles] = useState<string[]>([]);
  const [createFolder, setCreateFolder] = useState<boolean>(false);
  const [folderExists, setFolderExists] = useState<boolean>(false);
  const [folderValue, setFolderValue] = useState<string>("");
  const theme = useTheme();
  // Filter documents based on parentId and search text, similar to DocumentsGrid
  const allGridRows = originalTree.filter(
    (doc) =>
      doc.parentId === parentId && !selectedDocuments.some((selected) => selected.id === doc.id)
  );

  // Apply search filter if there's search text
  const filteredDocuments = searchText
    ? allGridRows.filter((file) =>
        file.documentName.toLowerCase().includes(searchText.toLowerCase())
      )
    : allGridRows;
  const columns = [
    {
      renderCell: (params: GridRenderCellParams) => {
        const duplicated = duplicatedFiles.find((d) => d === params.row.documentName);
        return (
          <Tooltip title={!duplicated && params ? params.row.documentName : null} arrow>
            <div className="flex align-items-center gap-8">
              <DocumentIcon fileType={params.row.fileType} />
              <Link
                style={{
                  color:
                    params.row.fileType === TYPE_FOLDER ? theme.palette.primary.main : "#515152",
                  textDecoration: "none",
                  maxWidth: "80%",
                  cursor: params.row.fileType === TYPE_FOLDER ? "pointer" : "default",
                }}
                className="text-overflow-ellipsis"
                data-testid={`link-${params.row.documentName}`}
                to={`#`}
                onClick={(e) => {
                  e.preventDefault();
                  handleColumnNameClick(params.row);
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  handleColumnNameClick(params.row);
                }}
                onMouseDown={(e) => {
                  if (e.button === 1) {
                    // Detection of the mouse scroll button
                    handleColumnNameClick(params.row);
                  }
                }}
                target="_blank"
              >
                {params.row.documentName}
              </Link>
              {hasTeamsSource(params.row) && (
                <Icon icon={`logos:microsoft-teams`} width={15} height={15} />
              )}
              {duplicated && (
                <Tooltip title={OVERRIDE_MESSAGE} arrow>
                  <Icon
                    icon="ep:warning-filled"
                    height={15}
                    style={{ color: "black", marginLeft: "10px" }}
                    aria-label={`Warning: ${params.row.documentName} will be override.`}
                  />
                </Tooltip>
              )}
            </div>
          </Tooltip>
        );
      },
      headerName: "Document name",
      flex: 1.5,
      field: "documentName",
    },
    {
      field: "category",
      headerName: "Category",
      flex: 1,
    },
    {
      field: "fileType",
      headerName: "File type",
      flex: 0.5,
      renderCell: (params: GridRenderCellParams) => (
        <div className="fileType_bullet_container">
          <div
            className={`fileType_bullet ${params.row.fileType == TYPE_FOLDER ? "folder" : "file"}`}
          >
            {params.row.fileType}
          </div>
        </div>
      ),
    },
    {
      field: "source",
      headerName: "Source",
      flex: 0.5,
    },
    {
      field: "uploaded",
      headerName: "Uploaded",
      flex: 1,
    },
    {
      field: "uploadedBy",
      headerName: "Uploaded by",
      flex: 1,
    },
  ];
  const handleColumnNameClick = (blob: DocumentData) => {
    if (blob.fileType == TYPE_FOLDER) {
      //Just add information to the breadcrumb
      setSelectedPath([...selectedPath, { id: blob.id, name: blob.documentName }]);
    }
  };
  const addToRequest = (attributes: any, files: DocumentData[], folder?: string) => {
    files.forEach((file) => {
      if (file.fileType === TYPE_FOLDER) {
        // Get all children from the flat structure using the new utility function
        const children = getDocumentsWithChildren(
          [file.id],
          getBlobTree(originalAvailableFolders)
        ).filter(
          (child: DocumentData) => child.id !== file.id // Exclude the parent folder itself
        );
        addToRequest(attributes, children, folder + file.documentName + "/");
      } else if (!Object.hasOwn(attributes, file.fileAlias)) {
        attributes[file.fileAlias] = {
          category: file.category,
          metadata: file.metadata ?? "",
          path: folder,
          fileName: file.documentName,
          uuid: file.uuid,
        };
      }
    });
  };
  const validateDuplication = async (attributes: any) => {
    const keys = Object.keys(attributes);
    const validationResults = await callValidateDuplicate({
      fileNames: keys.map((k) => attributes[k].fileName),
      path: attributes[keys[0]].path,
    });
    return validationResults;
  };
  const moveToFolder = async () => {
    setIsLoading(true);
    const attributes: any = {};
    addToRequest(
      attributes,
      selectedDocuments,
      selectedPath.length > 0 ? selectedPath.map((p) => p.name).join("/") + "/" : ""
    );
    try {
      if (duplicatedFiles.length === 0) {
        const duplications = await validateDuplication(attributes);
        const duplicated = duplications.filter((d) => d.exists).map((d) => d.name);
        if (duplicated.length > 0) {
          setSelectedPath(
            attributes[Object.keys(attributes)[0]].path.split("/").filter((p: string) => p !== "")
          );
          setAlertColor(AlertColor.Warning);
          setAlertMessage(
            "A file with the same name already exists in the selected location. Select Continue to overwrite the existing file."
          );
          setDuplicatedFiles(duplicated);
          setIsLoading(false);
          return false;
        }
      }
      //Need to remove the duplicated files
      const filesToRemove = filteredDocuments
        .filter((d) => duplicatedFiles.find((dF) => dF === d.documentName))
        .map((f) => f.fileAlias);
      if (filesToRemove.length > 0) {
        await callDeleteBlobs(filesToRemove);
      }
      const url = `${process.env.REACT_APP_SERVER_HOST}/update`;
      const response = await axios.patch(
        url,
        { blobs: attributes, category_updated: false },
        {
          headers: {
            "Cache-Control": "no-store",
          },
        }
      );
      if (response.status == 200) {
        callbackClose(true);
      }
    } catch (e: any) {
      setAlertColor(AlertColor.Error);
      setAlertMessage(e.message || "Unexpected error while calling the API.");
    }
    setIsLoading(false);
  };

  useEffect(() => {
    setParentId(selectedPath.length === 0 ? null : (selectedPath.at(-1)?.id ?? null));
  }, [selectedPath]);

  useEffect(() => {
    setOriginalTree(
      getBlobTree(originalAvailableFolders).filter(
        (d: DocumentData) => !selectedDocuments.some((sD) => sD.id === d.id)
      )
    );
  }, [originalAvailableFolders, selectedDocuments]);
  const getMoveButtonContent = () => {
    if (isLoading) {
      return (
        <div style={{ display: "flex", alignItems: "center", padding: "0 10px" }}>
          <CircularProgress size={15} />
          <span style={{ marginLeft: 10 }}>Moving...</span>
        </div>
      );
    }
    if (duplicatedFiles.length > 0) {
      return "Continue moving";
    }
    return "Move here";
  };
  const isDisabledMoveButton = () => {
    if (isLoading) {
      return true;
    }
    const selectedPathJoined =
      selectedPath.length > 0 ? selectedPath.map((p) => p.name).join("/") + "/" : "";
    const currentPathJoined =
      currentPath.length > 0 ? currentPath.map((p) => p.name).join("/") + "/" : "";
    return currentPathJoined === selectedPathJoined;
  };
  return (
    <>
      <Modal
        open={true}
        onClose={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key !== "Escape") {
            callbackClose(false);
          }
        }}
        data-testid="moveModalFiles"
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          className="modal-kb-content"
          style={{
            height: "auto",
            width: "80vw",
            borderRadius: "4px",
            maxHeight: "90vh",
            overflowY: "auto",
          }}
          data-testid="moveModal"
        >
          <div className="modal-kb-header">
            <button onClick={() => callbackClose(false)} className="close_icon">
              <Icon icon="bitcoin-icons:cross-filled" height={30} color="#000000" />
            </button>
            <div className="flex flow-column">
              <span className="upload_title">Move to</span>
              <span>Move selected item(s) to desired folder.</span>
            </div>
          </div>
          <div className="modal-kb-body">
            <div className="flex flow-column">
              <div className="flex content_menu align-items-center">
                <div className="search-bar-container">
                  <SearchField searchText={searchText} setSearchText={setSearchText} />
                </div>
                {createFolder && (
                  <div className="flex gap-15">
                    <CreateFolderStep
                      folderPath={folderValue}
                      onKeyDownCallback={(e: React.KeyboardEvent<HTMLInputElement>) => {
                        if (e.key === "Escape") {
                          setCreateFolder(false);
                          setFolderValue("");
                        } else if (e.key === "Enter" && !folderExists) {
                          // Calculate current parent ID based on selectedPath
                          let currentParentId: string | null = null;
                          for (const pathSegment of selectedPath) {
                            const foundFolder = originalTree.find(
                              (doc) =>
                                doc.documentName === pathSegment.name &&
                                doc.parentId === currentParentId &&
                                doc.fileType === TYPE_FOLDER
                            );
                            if (foundFolder) {
                              currentParentId = foundFolder.id;
                            }
                          }

                          setOriginalAvailableFolders([
                            ...originalAvailableFolders,
                            {
                              documentName: folderValue,
                              file_name: folderValue,
                              indexing_status: "SUCCESS" as const,
                              fileType: TYPE_FOLDER,
                              category: "",
                              id: `folder-${selectedPath.map((p) => p.id).join("-")}-${folderValue}`,
                              metadata: "",
                              path:
                                selectedPath.length > 0
                                  ? selectedPath.map((p) => p.name).join("/") + "/"
                                  : "",
                              previewUrl: "",
                              uploaded: new Date().toISOString(),
                              uploadedBy: "",
                              url: "",
                              fileAlias: "",
                              folderSource: "",
                              source: "",
                              file_extension: `1.${TYPE_FOLDER}`,
                              uuid: `folder-${folderValue}`,
                              parentId: currentParentId,
                            },
                          ]);
                          setCreateFolder(false);
                          setFolderValue("");
                        }
                      }}
                      onChangeCallback={(e): boolean => {
                        const value = e.target.value;

                        // Navigate through the flat structure using parentId relationships
                        let currentParentId: string | null = null;
                        let folderExists = false;

                        // First navigate through selectedPath to get the current parent
                        for (const pathSegment of selectedPath) {
                          const foundFolder = originalTree.find(
                            (doc) =>
                              doc.documentName.trim() === pathSegment.name.trim() &&
                              doc.parentId === currentParentId &&
                              doc.fileType === TYPE_FOLDER
                          );
                          if (foundFolder) {
                            currentParentId = foundFolder.id;
                          } else {
                            break;
                          }
                        }

                        // Now check if the new folder name already exists at this location
                        const existingFolder = originalTree.find(
                          (doc) =>
                            doc.documentName.trim() === value.trim() &&
                            doc.parentId === currentParentId &&
                            doc.fileType === TYPE_FOLDER
                        );

                        folderExists = !!existingFolder;
                        setFolderValue(value);
                        setFolderExists(folderExists);
                        return folderExists;
                      }}
                    />
                  </div>
                )}
                {!createFolder && (
                  <button
                    className="upload_btn"
                    onClick={() => setCreateFolder(true)}
                    data-testid="createFolderButton"
                  >
                    <Icon icon="ph:folder-simple-plus-light" width={18} height={18} />
                    Create folder
                  </button>
                )}
              </div>
              <DocumentGridBreadcrumb
                breadcrumbs={selectedPath}
                clickOnPathCallback={setSelectedPath}
                disabled={duplicatedFiles.length > 0}
              />
              <CustomGrid
                style={{
                  maxHeight: "50vh",
                  minHeight: "40vh",
                  overflow: "visible",
                }}
                rows={filteredDocuments}
                columns={columns as GridColDef[]}
                allRows={originalTree}
                keepNonExistentRowsSelected={true}
                pageSizeOptions={[5, 10, 50, 100]}
                disableRowSelectionOnClick
                classes={{
                  footerContainer: "footer",
                }}
                localeText={{
                  MuiTablePagination: {
                    labelRowsPerPage: "Items per page:",
                  },
                }}
              />
            </div>
          </div>
          <div className="modal-kb-footer">
            <button
              className="save_btn"
              onClick={moveToFolder}
              disabled={isDisabledMoveButton()}
              data-testid="moveModal-moveButton"
            >
              {getMoveButtonContent()}
            </button>
            <button
              className="cancel_btn"
              onClick={() => {
                if (duplicatedFiles.length > 0) {
                  setDuplicatedFiles([]);
                  setSelectedPath(selectedPath.slice(0, selectedPath.length - 1));
                } else {
                  callbackClose(false);
                }
              }}
              data-testid="moveModal-cancelButton"
            >
              Cancel
            </button>
          </div>
        </div>
      </Modal>
      <Alerts
        style={{ top: "", right: "", left: "" }}
        alertOpen={alertMessage !== ""}
        color={alertColor}
        message={alertMessage}
        handleAlertClose={() => setAlertMessage("")}
      />
    </>
  );
};
